import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { AppDataContext } from "@src/components/AppDataContext";
import { _logStartExperiment, _logConvertExperiment, getSessionId } from "@src/utils/logClient";
import {
  hasPerformanceConsent,
  pickHomepageVariant,
  HOMEPAGE_VARIANT_COOKIE,
  HOMEPAGE_EXPERIMENT_SALT,
  HP_TEST_MAX,
  HP_CONTROL_MIN,
  HP_CONTROL_MAX,
} from "@src/utils/homepageExperimentBucketing";
import { useFeatureFlagsWithOverrides } from "@src/utils/useFeatureFlag";

// Client-side wiring for the homepage hero A/B experiment (defer-until-consent):
// bucket/log only with affirmative consent + a stable session id, so the shown arm
// equals the logged variant. Logging uses the normal switch-map path (no entityId).

export const HOMEPAGE_EXPERIMENT_ID = "web_external_homepage_experiment_hero";
export { HOMEPAGE_VARIANT_COOKIE };

// "test" = new MDS hero, "control" = legacy hero, "off" = not enrolled.
export type HomepageVariant = "control" | "test" | "off";

const VALID_VARIANTS: ReadonlySet<HomepageVariant> = new Set(["control", "test", "off"]);

export function readVariantCookie(): HomepageVariant | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((row) => row.startsWith(`${HOMEPAGE_VARIANT_COOKIE}=`));
  if (!match) return null;
  const value = match.slice(HOMEPAGE_VARIANT_COOKIE.length + 1);
  return VALID_VARIANTS.has(value as HomepageVariant) ? (value as HomepageVariant) : null;
}

function mayLogExperiment(): boolean {
  return hasPerformanceConsent();
}

// Persist only enrolled arms (test/control), never "off" — off visitors stay cookieless
// and get re-evaluated each visit, so a ramp-up absorbs them. Enrolled arms are sticky.
function writeVariantCookie(variant: HomepageVariant): void {
  if (typeof document === "undefined") return;
  if (variant === "off") return;
  document.cookie = `${HOMEPAGE_VARIANT_COOKIE}=${variant}; Max-Age=2592000; Path=/; Secure; SameSite=Lax`;
}

// Resolves the hero arm ("default" | "test", null until decided). Reuses a sticky
// decision, else buckets once consent + a real sessionId exist, then logs the start.
export function useHomepageHeroArm(): "default" | "test" | null {
  const [arm, setArm] = useState<"default" | "test" | null>(null);
  const featureFlags = useFeatureFlagsWithOverrides();
  const flagsRef = useRef(featureFlags);
  flagsRef.current = featureFlags;
  const startedRef = useRef(false);

  // Gate start logging on the session having loaded, so the log client's entityType is
  // set (else the start event is rejected for a missing entityType).
  const {
    data: { loadingSession },
  } = useContext(AppDataContext);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const resolve = async () => {
      let variant = readVariantCookie();

      // Bucket only with affirmative consent + a stable sessionId; otherwise stay control.
      if (!variant && hasPerformanceConsent()) {
        const sessionId = await getSessionId();
        if (sessionId && sessionId !== "redacted") {
          variant = pickHomepageVariant(
            sessionId,
            HOMEPAGE_EXPERIMENT_SALT,
            HP_TEST_MAX,
            HP_CONTROL_MIN,
            HP_CONTROL_MAX,
          );
          writeVariantCookie(variant);
        }
      }

      // CSS hides [data-hp-arm="test"] unless this attribute is set.
      if (variant === "test") {
        document.documentElement.setAttribute("data-hp-variant", "test");
        setArm("test");
      } else {
        setArm("default");
      }

      // Log start once for enrolled arms, after consent + session load.
      if (
        !startedRef.current &&
        !loadingSession &&
        (variant === "test" || variant === "control") &&
        hasPerformanceConsent()
      ) {
        const sessionId = await getSessionId();
        if (sessionId && sessionId !== "redacted") {
          startedRef.current = true;
          _logStartExperiment(HOMEPAGE_EXPERIMENT_ID, { ...flagsRef.current, [HOMEPAGE_EXPERIMENT_ID]: variant });
        }
      }
    };

    resolve();
    window.addEventListener("OneTrustGroupsUpdated", resolve);
    return () => {
      window.removeEventListener("OneTrustGroupsUpdated", resolve);
    };
    // Re-run when the session resolves so the start can fire.
  }, [loadingSession]);

  return arm;
}

// Convert callback for the constituent-button clicks. No-op unless the visitor is
// enrolled (sticky cookie) and has affirmative consent, so it's safe site-wide.
export function useHomepageExperimentConvert(): () => void {
  const featureFlags = useFeatureFlagsWithOverrides();
  const flagsRef = useRef(featureFlags);
  flagsRef.current = featureFlags;

  return useCallback(async () => {
    if (typeof window === "undefined") return;
    const variant = readVariantCookie();
    if (!variant) return;
    // "off" visitors are not in the experiment, so they shouldn't convert it either.
    if (variant === "off") return;
    if (!mayLogExperiment()) return;
    const sessionId = await getSessionId();
    if (!sessionId || sessionId === "redacted") return;
    _logConvertExperiment(HOMEPAGE_EXPERIMENT_ID, { ...flagsRef.current, [HOMEPAGE_EXPERIMENT_ID]: variant });
  }, []);
}
